<script setup lang="ts">
import { ref, computed } from 'vue';
import { Type, Eye, Share2, RefreshCw } from 'lucide-vue-next';
import ShowcaseMock from '@/components/features/showcase/ShowcaseMock.vue';

const activeStage = ref('reset');
const showRestart = ref(false);
const isUserInteracting = ref(false);
const showcaseRef = ref<any>(null);
const sectionRef = ref<HTMLElement | null>(null);
const mobileCardRef = ref<HTMLElement | null>(null);

const features = [
  // ... existing features ...
  { id: 'typing', title: 'TYPE IT', description: 'As simple as typing a bet. Easy and Fast. Type bet selections to build your bet slip. Singles, accumulators or even bet builders.', icon: Type },
  { id: 'see-it', title: 'SEE IT', description: 'Choose a BetPic from template library, ready to share into your chat and social media.', icon: Eye },
  { id: 'share-it', title: 'SHARE IT', description: 'Each BetPic links to your bet slip. Ready for friends to see, place, edit or share again.', icon: Share2 }
];

const handleStageChange = (stage: string) => {
  activeStage.value = stage;
  if (stage === 'completed') {
    showRestart.value = true;
  }
  if (stage === 'reset') {
    isUserInteracting.value = false;
  }
};

const isVisible = (featureId: string) => {
  const currentStage = activeStage.value;
  
  // If user clicked a card manually, only that specific one should be coloured
  if (isUserInteracting.value) {
    return currentStage === featureId;
  }
  
  // Always show typing as active/coloured from start in auto mode
  if (featureId === 'typing') return true;
  
  // Additive logic for automated demo
  if (currentStage === 'reset') return featureId === 'typing';
  if (currentStage === 'typing') return featureId === 'typing';
  if (currentStage === 'see-it') return featureId === 'typing' || featureId === 'see-it';
  if (currentStage === 'share-it' || currentStage === 'completed') return true;
  
  return false;
};

const restartDemo = () => {
  showRestart.value = false;
  isUserInteracting.value = false;
  if (showcaseRef.value) {
    showcaseRef.value.restart();
  }
};

const scrollToView = (el: HTMLElement | null) => {
  if (el) {
    if (window.innerWidth <= 768) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      const rect = el.getBoundingClientRect();
      const viewHeight = window.innerHeight;
      const center = rect.top + rect.height / 2;
      const viewCenter = viewHeight / 2;
      
      if (Math.abs(center - viewCenter) > 50) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
};

const jumpToStage = (stageId: string) => {
  isUserInteracting.value = true;
  showRestart.value = true; // Show restart button immediately on jump
  activeStage.value = stageId;
  if (showcaseRef.value) {
    showcaseRef.value.jumpToStage(stageId);
  }
  
  if (window.innerWidth <= 768) {
    scrollToView(mobileCardRef.value);
  } else {
    scrollToView(sectionRef.value);
  }
};

const displayFeature = computed(() => {
  const id = (activeStage.value === 'reset' || activeStage.value === 'typing') ? 'typing' : 
             (activeStage.value === 'completed' || activeStage.value === 'share-it') ? 'share-it' : 
             activeStage.value;
  return features.find(f => f.id === id);
});
</script>

<template>
  <section ref="sectionRef" class="features-section">
    
    <div class="section-title mt-8">
      <h2 class="animate-on-scroll">Create and Share <span class="text-gradient">Bet Slip Images</span></h2>
      <p class="animate-on-scroll">Turn your selections into a slick shareable image to share with friends and a link straight to the bet slip.</p>
    </div>

    <div class="features-content-wrapper">
      <div class="demo-side">
        <div class="showcase-wrapper">
          <ShowcaseMock ref="showcaseRef" @stage-change="handleStageChange" />
        </div>
      </div>

      <!-- Mobile Unified Card (Visible on Mobile) -->
      <div ref="mobileCardRef" class="unified-card-mobile glass animate-on-scroll">
        <div class="mobile-stepper-icons">
          <div class="icons-row-centered">
            <div 
              v-for="feature in features" 
              :key="'icon-' + feature.id"
              class="mobile-step-icon"
              :class="{ 'is-active': isVisible(feature.id) }"
              @click="jumpToStage(feature.id)"
            >
              <component :is="feature.icon" :size="20" />
            </div>
          </div>

          <!-- Restart Button (Absolute on Mobile) -->
          <transition name="fade">
            <button 
              v-if="showRestart" 
              @click="restartDemo" 
              class="mobile-step-icon mobile-restart-btn-inline"
            >
              <RefreshCw :size="18" />
            </button>
          </transition>
        </div>
        
        <div class="mobile-content-area">
          <transition name="fade-slide" mode="out-in">
            <div :key="displayFeature?.id" class="m-content">
              <h3>{{ displayFeature?.title }}</h3>
              <p>{{ displayFeature?.description }}</p>
            </div>
          </transition>
        </div>

      </div>

      <!-- Desktop Cards Stack (Hidden on Mobile) -->
      <div class="cards-stack desktop-only">
        <div 
          v-for="feature in features" 
          :key="feature.title" 
          class="feature-card glass"
          :class="{ 'is-active': isVisible(feature.id) }"
          @click="jumpToStage(feature.id)"
        >
          <div class="card-icon">
            <component :is="feature.icon" :size="20" />
          </div>
          <div class="card-content">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </div>

        <!-- Restart Button -->
        <transition name="fade">
          <button 
            v-if="showRestart" 
            @click="restartDemo" 
            class="restart-btn-fixed glass"
          >
            <RefreshCw :size="18" />
            <span>Restart</span>
          </button>
        </transition>
      </div>
    </div>
  </section>
</template>

<style scoped>
.unified-card-mobile {
  width: 100%;
  max-width: 500px;
  padding: 8px 12px; 
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px; 
  background: rgba(255, 255, 255, 0.02);
  order: 1; /* Ensure it stays at top on mobile */
  scroll-margin-top: 56px;
}

.cards-stack.desktop-only {
  display: none;
}

@media (min-width: 1024px) {
  .cards-stack.desktop-only {
    display: flex;
  }
  .unified-card-mobile {
    display: none !important; /* Use important to be absolutely sure */
  }
}

.mobile-stepper-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.icons-row-centered {
  display: flex;
  gap: 12px;
}

.mobile-step-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  filter: grayscale(1);
  opacity: 0.4;
}

.mobile-step-icon.is-active {
  background: var(--grad-main);
  color: white;
  filter: grayscale(0);
  opacity: 1;
  box-shadow: 0 8px 20px rgba(230, 0, 0, 0.3);
  transform: scale(1.1);
}

.mobile-content-area {
  min-height: 64px; 
  text-align: center;
}

.m-content h3 {
  font-size: 1rem;
  font-weight: 800;
  margin-bottom: 2px;
  color: var(--red-primary);
  letter-spacing: 0.5px;
}

.m-content p {
  color: var(--text-secondary);
  font-size: 0.8rem;
  line-height: 1.3;
}

.mobile-restart-btn-inline {
  position: absolute;
  right: 0;
  background: rgba(226, 27, 13, 0.1) !important;
  border: 1px solid rgba(226, 27, 13, 0.3) !important;
  color: var(--red-primary) !important;
  opacity: 1 !important;
  filter: grayscale(0) !important;
}

.mobile-restart-btn-inline:hover {
  background: rgba(226, 27, 13, 0.2) !important;
  transform: scale(1.1);
}

.restart-btn-fixed {
  position: absolute;
  top: calc(100% + 20px);
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(226, 27, 13, 0.1);
  border: 1px solid rgba(226, 27, 13, 0.3);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
}

.restart-btn-fixed:hover {
  background: rgba(226, 27, 13, 0.2);
  border-color: var(--red-primary);
  transform: translateY(-2px);
}

.features-section {
  padding: 80px 5%; /* Reduced from 120px */
  max-width: 1400px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .features-section { 
    padding: 10px 5%; /* Aggressively reduced */
  }
  .features-content-wrapper { gap: 4px; } 
}


.features-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
  align-items: center;
}

.cards-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 500px;
  position: relative;
}

.feature-card {
  display: flex;
  gap: 20px;
  padding: 24px;
  border-radius: 20px;
  opacity: 0.3;
  filter: grayscale(1);
  transform: translateX(20px);
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.feature-card.is-active {
  opacity: 1;
  filter: grayscale(0);
  transform: translateX(0);
  border-color: var(--red-primary);
  background: rgba(226, 27, 13, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.feature-card:hover {
  opacity: 0.8;
  transform: translateX(5px);
}

.feature-card.is-active:hover {
  opacity: 1;
  transform: scale(1.02);
}

.card-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  background: var(--grad-main);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 15px rgba(230, 0, 0, 0.2);
}

.card-content h3 {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.card-content p {
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
}

.demo-side {
  width: 100%;
  max-width: 800px;
  display: flex;
  justify-content: center;
  order: 2; /* Move demo below cards on mobile */
}

.showcase-wrapper {
  width: 100%;
  transform: scale(0.85);
  transform-origin: top center;
}

@media (max-width: 768px) {
  .demo-side {
    margin-top: -65px; /* Halved visual space (from -40px to -65px) */
  }
}

@media (min-width: 1024px) {
  .features-content-wrapper {
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    min-height: 700px;
  }

  .demo-side {
    order: 1; /* Restore normal order for desktop */
  }

  .cards-stack {
    margin: 0;
    order: 2; /* Restore normal order for desktop */
  }

  .showcase-wrapper {
    transform: scale(1);
    transform-origin: center center;
  }
}

</style>
